import { Plus, Trash2, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createCompany, getCompanyFilterValues } from "../../interfaces/companies";
import { createSignal } from "../../interfaces/signals";
import { updateEvent } from "../../interfaces/events";
import { IFounder, IContact, CompanySize, FundingStage, Priority, ITags } from "../../interfaces/company";
import { EventType, ImportanceLevel } from "../../interfaces/signal";

interface PrefilledData {
    companyName: string;
    companyDisplayName: string;
    companyWebsite: string;
    companyIndustry: string;
    companyLocation: string;
    companyDescription: string;
    companyPhone: string;
    companyGoogleRating?: number;
    companyGoogleReviewCount?: number;
    companyGoogleMapsUrl: string;
    companyPriority: Priority;
    companyTags: string[];
    companySiren?: string;
    signalTitle: string;
    signalDescription: string;
    signalEventType: EventType;
    signalSource: string;
    signalSourceUrl: string;
    signalImageUrl: string;
    signalImportance: ImportanceLevel;
    signalTags: string[];
    contactPhone: string;
    contacts: IContact[];
    sourceEventId: string;
    detectedDate?: string;
}

export default function CreateCompanySignalForm() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: Company, 2: 3: Review

    // Company form data
    const [companyData, setCompanyData] = useState({
        name: "",
        displayName: "",
        imageUrl: "",
        website: "",
        siren: "",
        description: "",
        industry: "",
        location: "",
        size: "" as CompanySize | "",
        fundingStage: "" as FundingStage | "",
        founders: [] as IFounder[],
        contacts: [] as IContact[],
        tags: {
            franchise: "",
            categories: [] as string[],
            subCategories: [] as string[],
        } as ITags,
        brandTags: [] as string[], // DEPRECATED
        notes: "",
        priority: "medium" as Priority,
        nextAction: "",
    });

    // Signal form data
    const [signalData, setSignalData] = useState({
        title: "",
        description: "",
        eventType: "other" as EventType,
        company: "",
        companyDisplayName: "",
        companyWebsite: "",
        companyLogo: "",
        companyIndustry: "",
        companyPhone: "",
        companyLinkedin: "",
        companyGoogleRating: undefined as number | undefined,
        companyGoogleReviewCount: undefined as number | undefined,
        companyGoogleMapsUrl: "",
        contactFirstName: "",
        contactLastName: "",
        contactTitle: "",
        contactEmail: "",
        contactLinkedin: "",
        contactPhone: "",
        currency: "",
        investors: [] as string[],
        valuation: undefined as number | undefined,
        source: "",
        sourceUrl: "",
        imageUrl: "",
        publishedAt: new Date() as Date,
        importance: "medium" as ImportanceLevel,
        tags: [] as string[],
        isActive: true,
        alreadyContacted: false,
    });

    const [publishedDate, setPublishedDate] = useState("");
    const [publishedTime, setPublishedTime] = useState("");

    const [sourceEventId, setSourceEventId] = useState<string>("");
    const [tagInput, setTagInput] = useState("");
    
    // États pour les nouveaux champs de tags
    const [categoryInput, setCategoryInput] = useState("");
    const [subCategoryInput, setSubCategoryInput] = useState("");
    
    // États pour les suggestions de tags
    const [tagSuggestions, setTagSuggestions] = useState<{
        franchises: string[];
        categories: string[];
        subCategories: string[];
    }>({
        franchises: [],
        categories: [],
        subCategories: [],
    });

    // Sync date/time inputs with publishedAt
    useEffect(() => {
        if (publishedDate && publishedTime) {
            const combinedDateTime = new Date(`${publishedDate}T${publishedTime}`);
            if (!isNaN(combinedDateTime.getTime())) {
                setSignalData(prev => ({ ...prev, publishedAt: combinedDateTime }));
            }
        }
    }, [publishedDate, publishedTime]);

    // Load tag suggestions from database
    useEffect(() => {
        const loadTagSuggestions = async () => {
            try {
                const tags = await getCompanyFilterValues('tags');
                setTagSuggestions(tags);
            } catch (error) {
                console.error("Error loading tag suggestions:", error);
            }
        };
        loadTagSuggestions();
    }, []);

    // Load prefilled data on component mount
    useEffect(() => {
        const prefilledDataStr = localStorage.getItem('prefilled-company-signal-data');

        if (prefilledDataStr) {
            try {
                const prefilledData: PrefilledData = JSON.parse(prefilledDataStr);

                // Fill company data
                setCompanyData(prev => ({
                    ...prev,
                    name: prefilledData.companyName,
                    displayName: prefilledData.companyDisplayName,
                    website: prefilledData.companyWebsite,
                    industry: prefilledData.companyIndustry,
                    location: prefilledData.companyLocation,
                    description: prefilledData.companyDescription,
                    priority: prefilledData.companyPriority,
                    brandTags: [],
                    contacts: prefilledData.contacts,
                    siren: prefilledData.companySiren || "",
                }));

                // Fill signal data
                // Use detectedDate or current date/time for publishedAt
                const detectedDateTime = prefilledData.detectedDate ? new Date(prefilledData.detectedDate) : new Date();
                const dateStr = detectedDateTime.toISOString().split('T')[0];
                const timeStr = detectedDateTime.toTimeString().slice(0, 5);
                
                setPublishedDate(dateStr);
                setPublishedTime(timeStr);
                
                setSignalData(prev => ({
                    ...prev,
                    title: prefilledData.signalTitle,
                    description: prefilledData.signalDescription,
                    eventType: prefilledData.signalEventType,
                    company: prefilledData.companyName,
                    companyDisplayName: prefilledData.companyDisplayName,
                    companyWebsite: prefilledData.companyWebsite,
                    companyLocation: prefilledData.companyLocation,
                    companyIndustry: prefilledData.companyIndustry,
                    companyPhone: prefilledData.companyPhone,
                    companyGoogleRating: prefilledData.companyGoogleRating,
                    companyGoogleReviewCount: prefilledData.companyGoogleReviewCount,
                    companyGoogleMapsUrl: prefilledData.companyGoogleMapsUrl,
                    source: prefilledData.signalSource,
                    sourceUrl: prefilledData.signalSourceUrl,
                    imageUrl: prefilledData.signalImageUrl,
                    importance: prefilledData.signalImportance,
                    tags: prefilledData.signalTags,
                    publishedAt: detectedDateTime,
                }));

                // Fill contact info from first contact if available
                if (prefilledData.contacts.length > 0) {
                    const primaryContact = prefilledData.contacts.find(c => c.isPrimary) || prefilledData.contacts[0];
                    setSignalData(prev => ({
                        ...prev,
                        contactFirstName: primaryContact.firstName || "",
                        contactLastName: primaryContact.lastName || "",
                        contactTitle: primaryContact.title || "",
                        contactEmail: primaryContact.email || "",
                        contactLinkedin: primaryContact.linkedinUrl || "",
                        contactPhone: primaryContact.phone || prefilledData.contactPhone || "",
                    }));
                }

                setSourceEventId(prefilledData.sourceEventId);
                console.log("✅ Data loaded successfully! Event ID:", prefilledData.sourceEventId);
                
                // Clear the data from localStorage
                localStorage.removeItem('prefilled-company-signal-data');
                console.log("🧹 Cleared localStorage");
            } catch (error) {
                console.error("❌ Error parsing prefilled data:", error);
            }
        }
    }, []);

    const addContact = () => {
        setCompanyData(prev => ({
            ...prev,
            contacts: [...prev.contacts, {
                name: "",
                firstName: "",
                lastName: "",
                title: "",
                email: "",
                linkedinUrl: "",
                phone: "",
                isPrimary: prev.contacts.length === 0
            }]
        }));
    };

    const updateContact = (index: number, field: keyof IContact, value: any) => {
        setCompanyData(prev => ({
            ...prev,
            contacts: prev.contacts.map((contact, i) => 
                i === index ? { ...contact, [field]: value } : contact
            )
        }));
    };

    const removeContact = (index: number) => {
        setCompanyData(prev => ({
            ...prev,
            contacts: prev.contacts.filter((_, i) => i !== index)
        }));
    };

    const _handleAddTag = () => {
        if (tagInput.trim() && !companyData.brandTags.includes(tagInput.trim())) {
            setCompanyData(prev => ({
                ...prev,
                brandTags: [...prev.brandTags, tagInput.trim()]
            }));
            setTagInput("");
        }
    };

    const _handleRemoveTag = (tag: string) => {
        setCompanyData(prev => ({
            ...prev,
            brandTags: prev.brandTags.filter(t => t !== tag)
        }));
    };

    const handleAddCategory = () => {
        if (categoryInput.trim() && !companyData.tags.categories?.includes(categoryInput.trim())) {
            setCompanyData(prev => ({
                ...prev,
                tags: {
                    ...prev.tags,
                    categories: [...(prev.tags.categories || []), categoryInput.trim()]
                }
            }));
            setCategoryInput("");
        }
    };

    const handleRemoveCategory = (category: string) => {
        setCompanyData(prev => ({
            ...prev,
            tags: {
                ...prev.tags,
                categories: prev.tags.categories?.filter(c => c !== category) || []
            }
        }));
    };

    const handleAddSubCategory = () => {
        if (subCategoryInput.trim() && !companyData.tags.subCategories?.includes(subCategoryInput.trim())) {
            setCompanyData(prev => ({
                ...prev,
                tags: {
                    ...prev.tags,
                    subCategories: [...(prev.tags.subCategories || []), subCategoryInput.trim()]
                }
            }));
            setSubCategoryInput("");
        }
    };

    const handleRemoveSubCategory = (subCategory: string) => {
        setCompanyData(prev => ({
            ...prev,
            tags: {
                ...prev.tags,
                subCategories: prev.tags.subCategories?.filter(sc => sc !== subCategory) || []
            }
        }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            // Prepare company data with proper typing
            const companySubmitData = {
                ...companyData,
                size: companyData.size || undefined,
                fundingStage: companyData.fundingStage || undefined,
                tags: companyData.tags,
            };

            // Create company first
            const company = await createCompany(companySubmitData as any);

            // Prepare signal data with proper typing
            // Find primary contact or use the first one
            const primaryContact = companyData.contacts.find(c => c.isPrimary) || companyData.contacts[0];

            const signalSubmitData = {
                ...signalData,
                company: company.name,
                contactFirstName: primaryContact?.firstName || signalData.contactFirstName,
                contactLastName: primaryContact?.lastName || signalData.contactLastName,
                contactTitle: primaryContact?.title || signalData.contactTitle,
                contactEmail: primaryContact?.email || signalData.contactEmail,
                contactLinkedin: primaryContact?.linkedinUrl || signalData.contactLinkedin,
                contactPhone: primaryContact?.phone || signalData.contactPhone,
                publishedAt: signalData.publishedAt?.toISOString(),
                tags: companyData.tags, // Utiliser les nouveaux tags structurés
            };

            // Create signal
            await createSignal(signalSubmitData);

            // Mark the event as having a signal created
            if (sourceEventId) {
                await updateEvent(sourceEventId, { signalCreated: true });
            }

            alert("Company and signal created successfully!");
            navigate("/bo/events");
        } catch (error: any) {
            console.error("Error creating company and signal:", error);
            alert(error.response?.data?.error || "Failed to create company and signal");
        } finally {
            setLoading(false);
        }
    };

    const renderCompanyForm = () => (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                        <input
                            type="text"
                            value={companyData.name}
                            onChange={(e) => setCompanyData(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                        <input
                            type="text"
                            value={companyData.displayName}
                            onChange={(e) => setCompanyData(prev => ({ ...prev, displayName: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                            placeholder="e.g., Restaurant name"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">SIREN *</label>
                        <input
                            type="text"
                            value={companyData.siren}
                            onChange={(e) => setCompanyData(prev => ({ ...prev, siren: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                        <input
                            type="url"
                            value={companyData.website}
                            onChange={(e) => setCompanyData(prev => ({ ...prev, website: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                        <input
                            type="text"
                            value={signalData.title}
                            onChange={(e) => setSignalData(prev => ({ ...prev, title: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                            required
                        />
                    </div> */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Event Type *</label>
                        <select
                            value={signalData.eventType}
                            onChange={(e) => setSignalData(prev => ({ ...prev, eventType: e.target.value as EventType }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        >
                            <option value="hiring">Hiring</option>
                            <option value="restaurant_opening">Restaurant Opening</option>
                            <option value="restaurant_owner_change">Restaurant Owner Change</option>
                            <option value="multi_site">Multi site</option>
                            <option value="franchise_signing">Franchise Signing</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Importance</label>
                        <select
                            value={signalData.importance}
                            onChange={(e) => setSignalData(prev => ({ ...prev, importance: e.target.value as ImportanceLevel }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                        </select>
                    </div> */}
                    {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                        <input
                            type="text"
                            value={signalData.source}
                            onChange={(e) => setSignalData(prev => ({ ...prev, source: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        />
                    </div> */}
                    
                    {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Source URL</label>
                        <input
                            type="url"
                            value={signalData.sourceUrl}
                            onChange={(e) => setSignalData(prev => ({ ...prev, sourceUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        />
                    </div> */}
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                        <input
                            type="url"
                            value={signalData.imageUrl}
                            onChange={(e) => setSignalData(prev => ({ ...prev, imageUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                            placeholder="Optional image/logo URL"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Published Date</label>
                        <input
                            type="date"
                            value={publishedDate}
                            onChange={(e) => setPublishedDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Published Time</label>
                        <input
                            type="time"
                            value={publishedTime}
                            onChange={(e) => setPublishedTime(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        />
                    </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                        <input
                            type="text"
                            value={companyData.location}
                            onChange={(e) => setCompanyData(prev => ({ ...prev, location: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company Phone</label>
                        <input
                            type="tel"
                            value={signalData.companyPhone}
                            onChange={(e) => setSignalData(prev => ({ ...prev, companyPhone: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                        />
                    </div>
                </div>
                
                <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-medium text-gray-900">Contacts</h4>
                        <button
                            type="button"
                            onClick={addContact}
                            className="flex items-center gap-1 px-3 py-1 bg-[#1A1A1A] text-white rounded-md hover:bg-[#333] text-sm"
                        >
                            <Plus className="w-4 h-4" />
                            Add Contact
                        </button>
                    </div>
                    
                    {companyData.contacts.map((contact, index) => (
                        <div key={index} className="border border-gray-200 rounded-md p-4 mb-3">
                            <div className="flex justify-between items-center mb-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">Contact {index + 1}</span>
                                    <input
                                        type="checkbox"
                                        checked={contact.isPrimary || false}
                                        onChange={(e) => updateContact(index, 'isPrimary', e.target.checked)}
                                        className="rounded"
                                    />
                                    <label className="text-xs text-gray-600">Primary</label>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeContact(index)}
                                    className="text-red-600 hover:text-red-800"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    placeholder="First Name"
                                    value={contact.firstName || ""}
                                    onChange={(e) => updateContact(index, 'firstName', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                                />
                                <input
                                    type="text"
                                    placeholder="Last Name"
                                    value={contact.lastName || ""}
                                    onChange={(e) => updateContact(index, 'lastName', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                                />
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={contact.email || ""}
                                    onChange={(e) => updateContact(index, 'email', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                                />
                                <input
                                    type="text"
                                    placeholder="Title"
                                    value={contact.title || ""}
                                    onChange={(e) => updateContact(index, 'title', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                                />
                                <input
                                    type="url"
                                    placeholder="LinkedIn URL"
                                    value={contact.linkedinUrl || ""}
                                    onChange={(e) => updateContact(index, 'linkedinUrl', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 md:col-span-2"
                                />
                                <input
                                    type="tel"
                                    placeholder="Phone"
                                    value={contact.phone || ""}
                                    onChange={(e) => updateContact(index, 'phone', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 md:col-span-2"
                                />
                            </div>
                        </div>
                    ))}
                </div>
                
                {/* Tags Section */}
                <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Tags</h4>
                    
                    {/* Franchise */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Franchise</label>
                        <input
                            type="text"
                            list="franchise-suggestions"
                            value={companyData.tags.franchise || ""}
                            onChange={(e) => setCompanyData(prev => ({
                                ...prev,
                                tags: { ...prev.tags, franchise: e.target.value }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                            placeholder="e.g., Subway, McDonald's..."
                        />
                        <datalist id="franchise-suggestions">
                            {tagSuggestions.franchises.map((franchise, index) => (
                                <option key={index} value={franchise} />
                            ))}
                        </datalist>
                    </div>

                    {/* Categories */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Catégories</label>
                        <div className="flex gap-2 mb-2">
                            <input
                                type="text"
                                list="category-suggestions"
                                value={categoryInput}
                                onChange={(e) => setCategoryInput(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAddCategory();
                                    }
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                                placeholder="Ajouter une catégorie..."
                            />
                            <button
                                type="button"
                                onClick={handleAddCategory}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                            >
                                Ajouter
                            </button>
                        </div>
                        <datalist id="category-suggestions">
                            {tagSuggestions.categories.map((category, index) => (
                                <option key={index} value={category} />
                            ))}
                        </datalist>
                        {companyData.tags.categories && companyData.tags.categories.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {companyData.tags.categories.map((category, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-800 rounded-full border border-green-200"
                                    >
                                        {category}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveCategory(category)}
                                            className="hover:text-green-900"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* SubCategories */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sous-catégories</label>
                        <div className="flex gap-2 mb-2">
                            <input
                                type="text"
                                list="subcategory-suggestions"
                                value={subCategoryInput}
                                onChange={(e) => setSubCategoryInput(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAddSubCategory();
                                    }
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                                placeholder="Ajouter une sous-catégorie..."
                            />
                            <button
                                type="button"
                                onClick={handleAddSubCategory}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                            >
                                Ajouter
                            </button>
                        </div>
                        <datalist id="subcategory-suggestions">
                            {tagSuggestions.subCategories.map((subCategory, index) => (
                                <option key={index} value={subCategory} />
                            ))}
                        </datalist>
                        {companyData.tags.subCategories && companyData.tags.subCategories.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {companyData.tags.subCategories.map((subCategory, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-800 rounded-full border border-purple-200"
                                    >
                                        {subCategory}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveSubCategory(subCategory)}
                                            className="hover:text-purple-900"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="">
            <div className="max-w-4xl mx-auto ">
                <div className="bg-white border rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">Create Company & Signal</h1>
                            <p className="text-sm text-gray-600">Review and edit the pre-filled information from the event</p>
                        </div>
                        <button
                            onClick={() => window.close()}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Form Content */}
                    <div className="min-h-[150px]">
                        {step === 1 && renderCompanyForm()}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                        <div>
                            {step > 1 && (
                                <button
                                    onClick={() => setStep(step - 1)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-[#F9F6F3]"
                                    disabled={loading}
                                >
                                    Previous
                                </button>
                            )}
                        </div>
                        
                        <div className="flex gap-2">
                            <button
                                onClick={() => navigate("/bo/events")}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-[#F9F6F3]"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            
                            {step < 1 ? (
                                <button
                                    onClick={() => setStep(step + 1)}
                                    className="px-4 py-2 bg-[#1A1A1A] text-white rounded-md hover:bg-[#333]"
                                    disabled={loading}
                                >
                                    Next
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                                    disabled={loading || !companyData.name || !companyData.siren || !signalData.title}
                                >
                                    {loading ? "Creating..." : "Create Company & Signal"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}